import { axiosInstance } from './axiosInstance';
import type {
  UserRole,
  LoginRequest,
  LoginResponse,
  JwtPayload,
  TwoFactorSetupResponse,
  TwoFactorStatusResponse,
} from '../shared/types';

export type { UserRole, LoginRequest, LoginResponse, JwtPayload, TwoFactorSetupResponse, TwoFactorStatusResponse };

// ── API Functions ───────────────────────────────────────────────────

export async function loginApi(data: LoginRequest): Promise<LoginResponse> {
  const response = await axiosInstance.post<LoginResponse>('/auth/login', data);
  return response.data;
}

export async function verifyLogin2faApi(tempToken: string, code: string): Promise<LoginResponse> {
  const response = await axiosInstance.post<LoginResponse>('/auth/login/verify', { tempToken, code });
  return response.data;
}

export async function get2faStatusApi(): Promise<TwoFactorStatusResponse> {
  const response = await axiosInstance.get<TwoFactorStatusResponse>('/auth/2fa/status');
  return response.data;
}

export async function generate2faApi(): Promise<TwoFactorSetupResponse> {
  const response = await axiosInstance.post<TwoFactorSetupResponse>('/auth/2fa/generate');
  return response.data;
}

export async function enable2faApi(code: string): Promise<TwoFactorStatusResponse> {
  const response = await axiosInstance.post<TwoFactorStatusResponse>('/auth/2fa/enable', { code });
  return response.data;
}

export async function disable2faApi(opts: { code?: string; password?: string }): Promise<TwoFactorStatusResponse> {
  const response = await axiosInstance.post<TwoFactorStatusResponse>('/auth/2fa/disable', opts);
  return response.data;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ResetPasswordPayload {
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  message: string;
}

export async function forgotPasswordApi(email: string): Promise<ForgotPasswordResponse> {
  const response = await axiosInstance.post<ForgotPasswordResponse>('/auth/forgot-password', { email });
  return response.data;
}

export async function resetPasswordApi(payload: ResetPasswordPayload): Promise<ResetPasswordResponse> {
  const response = await axiosInstance.post<ResetPasswordResponse>('/auth/reset-password', payload);
  return response.data;
}

// ── JWT Helpers ─────────────────────────────────────────────────────

export function decodeJwt(token: string): JwtPayload | null {
  try {
    const base64Payload = token.split('.')[1];
    const payload = atob(base64Payload);
    return JSON.parse(payload) as JwtPayload;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token);
  if (!payload) return true;
  return Date.now() >= payload.exp * 1000;
}
